import React, { useCallback } from 'react';
import styled from 'astroturf/react';
import { RelatedPaper } from './types';
import { TextButtonLink } from 'js/design_system/link/TextButtonLink';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Body } from 'js/design_system/Body';
import trackClick from 'js/utils/trackClick';

// Lazy load openLoginModal to avoid pulling LoginModal into server bundle
const loadOpenLoginModal = async () => {
  const { openLoginModal } = await import('js/login_modal/LoginModalManager');
  return openLoginModal;
};

const RelatedPapersContainer = styled.aside`
  /* Since Attachment uses display: block, this uses margin to create space between the PDF page and the interstitial */
  margin-top: var(--spacing-xs-8, 8px);
  margin-bottom: var(--spacing-xs-8, 8px);
  background: var(--background-white);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-lg);
  padding: var(--spacing-xs-12, 12px);
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  z-index: 10; /* Ensures the interstitial is above the PDF page stacking context (z-index: 1) for click handling */

  @media (min-width: 1280px) {
    margin-top: var(--spacing-sm-24, 24px);
    margin-bottom: var(--spacing-sm-24, 24px);
    padding: var(--spacing-sm-24, 24px);
  }
`;

const SectionHeading = styled.div`
  border-bottom: 1px solid var(--stroke-dark, #141413);
  padding: var(--spacing-xs-8, 8px) 0 var(--spacing-xs-16, 16px) 0;
  width: 100%;

  @media (min-width: 1280px) {
    padding: var(--spacing-xs-16, 16px) 0 var(--spacing-xs-8, 8px) 0;
  }
`;

const RelatedPapersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-xs-16, 16px);
  font-family: 'DM Sans', sans-serif;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--spacing-sm-24, 24px);
  }
`;

const PaperCard = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacing-xs-12, 12px);
  padding: var(--spacing-xs-16, 16px);
  border: 1px solid var(--stroke-light);
  border-radius: var(--border-radius-radius-md);
  background: white;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
  overflow: hidden;

  &:hover {
    background: var(--background-beige-light, #f8f7f4);
    border-color: var(--stroke-black, #141413);
    text-decoration: none;
  }

  &:focus-within {
    outline: 2px solid var(--stroke-dark, #141413);
    outline-offset: 2px;
  }
`;

const PaperContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
  min-width: 0;
`;

const PaperTitleText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  white-space: pre-wrap;
`;

const PaperCTAs = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8, 8px);
  align-items: center;
  padding-top: var(--spacing-xs-4, 4px);
  margin-top: auto;
`;

const PaperThumbnail = styled.div`
  flex-shrink: 0;
  width: 61px;
  height: 88px;
  background: white;
  border: 1px solid var(--stroke-light);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export type RelatedPapersProps = {
  papers: RelatedPaper[];
  marginBottom?: boolean;
};

export const RelatedPapers: React.FC<RelatedPapersProps> = ({
  papers,
  marginBottom = true,
}) => {
  const handleDownloadClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>, paper: RelatedPaper) => {
      e.preventDefault();
      e.stopPropagation();
      if (typeof window === 'undefined') return;
      const openLoginModal = await loadOpenLoginModal();
      openLoginModal({
        postLoginRedirectUrl: paper.url,
        clickTrackingContext: {
          source: 'loswp--interstitial-related-papers-download',
        },
        registrationParams: {
          user_source: 'download',
        },
      });
    },
    [],
  );

  const handleCardClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) return;
    const url = e.currentTarget.dataset.paperUrl;
    const workId = e.currentTarget.dataset.workId;
    trackClick({
      source: 'loswp--interstitial-related-papers-card',
      entityId: workId ? Number(workId) : undefined,
    });
    if (url) window.location.assign(url);
  }, []);

  if (papers.length === 0) {
    return null;
  }

  return (
    <RelatedPapersContainer
      style={marginBottom ? undefined : { marginBottom: 0 }}
    >
      <SectionHeading>
        <Body size="md" bold as="p">
          Related papers
        </Body>
      </SectionHeading>
      <RelatedPapersGrid>
        {papers.map((paper, index) => (
          <PaperCard
            key={index}
            data-paper-url={paper.url}
            data-work-id={paper.workId}
            onClick={handleCardClick}
          >
            <PaperContent>
              {/* Title as clickable link */}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <a
                href={paper.url}
                className="paper-card__title-link"
                style={{ textDecoration: 'none', color: 'inherit' }}
                rel={paper.nofollow ? 'nofollow' : undefined}
                onClick={() =>
                  trackClick({
                    source: 'loswp--interstitial-related-papers-title',
                    entityId: paper.workId,
                  })
                }
              >
                <Body size="sm" bold as="p">
                  <PaperTitleText>{paper.title}</PaperTitleText>
                </Body>
              </a>

              {/* Authors with comma separators */}
              {paper.authors && paper.authors.length > 0 && (
                <Body size="xs" as="div">
                  {paper.authors.map((author, idx) => (
                    <React.Fragment key={idx}>
                      <BodyLink
                        size="xs"
                        href={author.url}
                        rel={author.nofollow ? 'nofollow' : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          trackClick({
                            source: 'loswp--interstitial-related-papers-author',
                            entityId: paper.workId,
                          });
                        }}
                      >
                        {author.name}
                      </BodyLink>
                      {idx < paper.authors.length - 1 && <span>, </span>}
                    </React.Fragment>
                  ))}
                </Body>
              )}

              {/* Download CTA */}
              <PaperCTAs>
                {paper.isDownloadable && (
                  <TextButtonLink
                    href={paper.url}
                    icon="download"
                    size="sm"
                    inline={true}
                    onClick={(e) => handleDownloadClick(e, paper)}
                  >
                    Download free PDF
                  </TextButtonLink>
                )}
              </PaperCTAs>
            </PaperContent>

            {/* Thumbnail */}
            {paper.thumbnailUrl && (
              <PaperThumbnail>
                <img src={paper.thumbnailUrl} alt="" loading="lazy" />
              </PaperThumbnail>
            )}
          </PaperCard>
        ))}
      </RelatedPapersGrid>
    </RelatedPapersContainer>
  );
};
